import React, { useMemo, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { Fields, FormRef } from 'common/form';
import Yup from 'utils/validation';
import { BaseQuestion } from './BaseQuestion';
import { QuestionExpectedAnswer } from 'generated-graphql/types';
import { QuestionWithId, QuestionUpdates } from 'pages/CampaignCreation/types';
import { KnockOutQuestionView } from '../QuestionViews';
import { QualifyingQuestionView } from '../QuestionViews';

const knockoutListSx: SxProps<Theme> = {
  margin: 0,
  marginTop: '8px',
  paddingLeft: '20px',
  fontWeight: 400,
  '& li': {
    marginBottom: '4px'
  },
  '& li::marker': {
    fontSize: '10px'
  }
};

const KnockoutCheckboxLabel = ({ showDetails }: { showDetails: boolean }) => (
  <>
    Knockout question
    {showDetails && (
      <Box component="ul" sx={knockoutListSx}>
        <li>Qualifies or disqualifies candidates immediately</li>
        <li>Ensure the question can only be answered "yes" or "no"</li>
      </Box>
    )}
  </>
);

const textQuestionSchema = Yup.object({
  content: Yup.string().required('Enter a question.'),
  isKnockout: Yup.boolean(),
  expectedAnswer: Yup.string()
    .oneOf([QuestionExpectedAnswer.IsYes, QuestionExpectedAnswer.IsNo])
    .nullable(),
  preferredAnswerText: Yup.string()
});

export type TextQuestionFormData = Yup.InferType<typeof textQuestionSchema>;

type TextQuestionProps = {
  question: QuestionWithId;
  isFirst: boolean;
  isLast: boolean;
  updateQuestion: (id: string, updates: QuestionUpdates) => void;
  removeQuestion: (id: string) => void;
  moveQuestion: ({
    id,
    direction
  }: {
    id: string;
    direction: 'up' | 'down';
  }) => void;
};

export const TextQuestion: React.FC<TextQuestionProps> = ({
  question,
  isFirst,
  isLast,
  updateQuestion,
  removeQuestion,
  moveQuestion
}) => {
  const { id, content, expectedAnswer, preferredAnswerText, isMandatory } =
    question;
  const [isKnockout, setIsKnockout] = useState(isMandatory ?? false);

  const getFormFieldsConfig = (
    isKnockoutChecked: boolean
  ): Fields<TextQuestionFormData> => ({
    content: {
      type: 'textarea',
      label: 'Question',
      rows: 2
    },
    isKnockout: {
      type: 'checkbox',
      checkboxLabel: <KnockoutCheckboxLabel showDetails={isKnockoutChecked} />,
      controlSx: {
        alignItems: 'flex-start'
      }
    },
    expectedAnswer: {
      type: 'radio',
      label: 'Correct answer',
      options: [
        { value: QuestionExpectedAnswer.IsYes, label: 'Yes' },
        { value: QuestionExpectedAnswer.IsNo, label: 'No' }
      ],
      defaultValue: QuestionExpectedAnswer.IsYes,
      hidden: !isKnockoutChecked
    },
    preferredAnswerText: {
      type: 'textarea',
      label: 'Preferred answer (optional)',
      subLabel: "The candidate's answer will be scored against this answer.",
      rows: 3,
      hidden: isKnockoutChecked
    }
  });

  const formFieldsConfig = useMemo(
    () => getFormFieldsConfig(isKnockout),
    [isKnockout]
  );

  const handleFormFieldChange = (
    formRef: FormRef<TextQuestionFormData>,
    questionId: string
  ) => {
    return formRef.watch(
      (
        formValues: TextQuestionFormData,
        { name: changedFieldName }: { name: string }
      ) => {
        if (changedFieldName === 'content') {
          updateQuestion(questionId, { content: formValues.content });
        }

        if (changedFieldName === 'isKnockout') {
          const isNowKnockout = formValues.isKnockout ?? false;
          setIsKnockout(isNowKnockout);

          // Update the question type - preserve preferred answer data for when user switches back
          if (isNowKnockout) {
            // Converting to knockout: set isMandatory, add default expectedAnswer if not set
            updateQuestion(questionId, {
              isMandatory: true,
              expectedAnswer:
                question.expectedAnswer || QuestionExpectedAnswer.IsYes
            });
          } else {
            // Converting to standard: clear isMandatory and expectedAnswer
            updateQuestion(questionId, {
              isMandatory: false,
              expectedAnswer: null
            });
          }
        }

        if (changedFieldName === 'expectedAnswer' && isKnockout) {
          updateQuestion(questionId, {
            expectedAnswer: formValues.expectedAnswer as QuestionExpectedAnswer
          });
        }

        if (changedFieldName === 'preferredAnswerText' && !isKnockout) {
          updateQuestion(questionId, {
            preferredAnswerText: formValues.preferredAnswerText,
            hasPreferredAnswer: !!formValues.preferredAnswerText
          });
        }
      }
    ).unsubscribe;
  };

  const handleFormCancel = (initialFormValues: TextQuestionFormData) => {
    if (!initialFormValues.content) {
      // Delete the question if it's a new question (empty content)
      removeQuestion(id);
      return true;
    }

    // Reset to initial values - preserve preferred answer data
    const wasInitiallyKnockout = initialFormValues.isKnockout ?? false;
    setIsKnockout(wasInitiallyKnockout);

    updateQuestion(id, {
      content: initialFormValues.content,
      isMandatory: wasInitiallyKnockout,
      expectedAnswer: wasInitiallyKnockout
        ? (initialFormValues.expectedAnswer as QuestionExpectedAnswer) ??
          QuestionExpectedAnswer.IsYes
        : null,
      preferredAnswerText: initialFormValues.preferredAnswerText ?? '',
      hasPreferredAnswer: !!initialFormValues.preferredAnswerText
    });

    return true;
  };

  const renderViewMode = (editing: boolean) => {
    if (editing) return null;

    if (isMandatory) {
      return (
        <KnockOutQuestionView
          content={content}
          expectedAnswer={expectedAnswer || null}
        />
      );
    }

    return (
      <Box sx={{ flex: 1 }}>
        <QualifyingQuestionView
          content={content}
          preferredAnswerText={preferredAnswerText}
          onRequirementsGenerated={(requirements) =>
            updateQuestion(id, {
              preferredAnswerRequirements: requirements
            })
          }
        />
      </Box>
    );
  };

  const initialFormValues: TextQuestionFormData = {
    content,
    isKnockout: isMandatory ?? false,
    expectedAnswer: expectedAnswer || QuestionExpectedAnswer.IsYes,
    preferredAnswerText: preferredAnswerText ?? ''
  };

  return (
    <BaseQuestion<TextQuestionFormData>
      id={id}
      isFirst={isFirst}
      isLast={isLast}
      editByDefault={content === ''}
      setQuestionValue={(id, value) => updateQuestion(id, { content: value })}
      onDelete={() => removeQuestion(id)}
      moveQuestion={moveQuestion}
      schema={textQuestionSchema}
      fields={formFieldsConfig}
      formDefaultValues={initialFormValues}
      renderViewMode={renderViewMode}
      onWatch={handleFormFieldChange}
      onCancel={handleFormCancel}
    />
  );
};
